// v2.11.0 — Page publique /event/:address_civ
// Affiche un event en grand format avec QR code + bouton WhatsApp + CTA Réserver.
// Accessible sans auth — anonyme via userId localStorage.

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { getCategoryImage } from "../data/categoryImages";
import PaymentMockModal from "../components/PaymentMockModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_USER_ID = "civ_ai_user_id";

function _getOrCreateUserId() {
  try {
    let id = localStorage.getItem(STORAGE_USER_ID);
    if (!id) {
      id = "u_" + (crypto.randomUUID ? crypto.randomUUID().slice(0, 8) : Date.now().toString(36));
      localStorage.setItem(STORAGE_USER_ID, id);
    }
    return id;
  } catch (_) { return "u_anon"; }
}

function _formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
      + " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch (_) { return iso; }
}

const STATUS_BADGES = {
  live_now: { txt: "🔴 EN COURS", bg: "#DC2626" },
  today:    { txt: "AUJOURD'HUI", bg: "#F59E0B" },
  tomorrow: { txt: "DEMAIN", bg: "#0EA5E9" },
  upcoming: { txt: "À VENIR", bg: "#7C3AED" },
};

export default function EventPublicPage() {
  const { address_civ } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const userId = _getOrCreateUserId();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/event/by_address_civ/${encodeURIComponent(address_civ)}`)
      .then((r) => { setEvent(r.data.event); setLoading(false); })
      .catch((e) => { setError(e.response?.status === 404 ? "not_found" : "error"); setLoading(false); });
  }, [address_civ]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div data-testid="event-loading">Chargement…</div>
      </div>
    );
  }
  if (error || !event) {
    return (
      <div style={{ minHeight: "100vh", background: "#0f172a", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        <div data-testid="event-not-found" style={{ fontSize: 48, marginBottom: 12 }}>🤷</div>
        <h2 style={{ margin: 0 }}>Event introuvable</h2>
        <p style={{ color: "#94a3b8", maxWidth: 380, marginTop: 8 }}>
          Le code <code style={{ background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>{address_civ}</code> ne correspond à aucun event actif.
        </p>
        <Link to="/" style={{ color: "#0EA5E9", marginTop: 16 }} data-testid="event-back-home">← Retour à l'accueil</Link>
      </div>
    );
  }

  const isFree = !event.ticket_price_fcfa || event.ticket_price_fcfa === 0;
  const heroSrc = event.image_override || (getCategoryImage("live") || {}).src || null;
  const statusBadge = STATUS_BADGES[event.status] || STATUS_BADGES.upcoming;

  // URL absolue pour le QR code (navigation GPS).
  const navUrl = `${window.location.origin}/navigate?dest=${event.lat},${event.lng}&code=${encodeURIComponent(event.address_civ)}&label=${encodeURIComponent(event.venue_name || event.title)}`;

  // Lien WhatsApp pre-filled.
  const waText = `Bonjour, je souhaite réserver pour l'event "${event.title}" le ${_formatDate(event.starts_at)} à ${event.venue_name || ""}. Adresse : ${event.address_civ}`;
  const waUrl = event.whatsapp_contact
    ? `https://wa.me/${event.whatsapp_contact.replace(/[^\d]/g, "")}?text=${encodeURIComponent(waText)}`
    : `https://wa.me/?text=${encodeURIComponent(waText)}`;

  const onRsvp = async () => {
    try {
      const r = await axios.post(`${API}/event/rsvp`, { userId, address_civ: event.address_civ });
      toast.success(`✅ Présence confirmée — total ${r.data.total_rsvp} RSVP`);
      setEvent({ ...event, rsvp_count: r.data.total_rsvp });
    } catch (_) { toast.error("Impossible de confirmer"); }
  };

  const onShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text: event.description, url: window.location.href });
      } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Lien copié dans le presse-papier");
      } catch (_) { toast.error("Impossible de copier"); }
    }
  };

  return (
    <div data-testid="event-public-page" style={{ minHeight: "100vh", background: "#0f172a", color: "#fff", paddingBottom: 80 }}>
      {/* HERO */}
      <div style={{ position: "relative", height: 260, overflow: "hidden" }}>
        {heroSrc && (
          <img src={heroSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.2) 0%, rgba(15,23,42,0.9) 100%)" }} />
        <Link to="/" data-testid="event-link-home" style={{
          position: "absolute", top: 16, left: 16,
          background: "rgba(15,23,42,0.7)", color: "#fff",
          padding: "6px 12px", borderRadius: 999, textDecoration: "none",
          fontSize: 12, fontWeight: 600, border: "1px solid rgba(255,255,255,0.15)",
        }}>← Accueil</Link>
        <span data-testid="event-status-badge" style={{
          position: "absolute", top: 16, right: 16,
          background: statusBadge.bg, color: "#fff",
          padding: "4px 12px", borderRadius: 999,
          fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
        }}>{statusBadge.txt}</span>
      </div>

      {/* CONTENU */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "20px 16px 0", marginTop: -60, position: "relative", zIndex: 2 }}>
        <span data-testid="event-subcat-pill" style={{
          display: "inline-block", background: "rgba(168,85,247,0.18)", color: "#c4b5fd",
          padding: "2px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.06em", border: "1px solid rgba(168,85,247,0.35)",
        }}>{event.sub_category}</span>
        <h1 data-testid="event-title" style={{ fontSize: 28, fontWeight: 800, margin: "8px 0", lineHeight: 1.2 }}>{event.title}</h1>
        <div style={{ fontSize: 14, color: "#cbd5e1", lineHeight: 1.5 }}>
          📍 {event.venue_name} · {event.commune}<br />
          🕐 {_formatDate(event.starts_at)}
        </div>
        {event.description && (
          <p data-testid="event-description" style={{ fontSize: 14, color: "#e2e8f0", lineHeight: 1.6, marginTop: 14 }}>
            {event.description}
          </p>
        )}

        {/* PRIX */}
        <div data-testid="event-price-block" style={{
          marginTop: 18, padding: 16, borderRadius: 12,
          background: isFree ? "rgba(22,163,74,0.12)" : "rgba(245,158,11,0.12)",
          border: `1px solid ${isFree ? "rgba(22,163,74,0.35)" : "rgba(245,158,11,0.35)"}`,
        }}>
          <div style={{ fontSize: 11, color: isFree ? "#86efac" : "#fbbf24", letterSpacing: "0.05em", fontWeight: 700 }}>
            {isFree ? "ACCÈS LIBRE" : "TICKET"}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", marginTop: 2 }}>
            {isFree ? "Gratuit" : `${event.ticket_price_fcfa.toLocaleString("fr-FR")} FCFA`}
          </div>
          {event.rsvp_count > 0 && (
            <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 4 }}>
              👥 {event.rsvp_count} {event.rsvp_count > 1 ? "personnes intéressées" : "personne intéressée"}
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 18 }}>
          {isFree ? (
            <button
              onClick={onRsvp}
              data-testid="event-rsvp-btn"
              style={{ background: "#16A34A", color: "#fff", border: "none", padding: "12px", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 14, gridColumn: "1 / -1" }}
            >✅ Confirmer ma présence</button>
          ) : (
            <button
              onClick={() => setPaymentOpen(true)}
              data-testid="event-reserve-btn"
              style={{ background: "#F59E0B", color: "#0f172a", border: "none", padding: "12px", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontSize: 14, gridColumn: "1 / -1" }}
            >🎟 Réserver — {event.ticket_price_fcfa.toLocaleString("fr-FR")} FCFA</button>
          )}
          <a
            href={waUrl}
            target="_blank" rel="noopener noreferrer"
            data-testid="event-whatsapp-btn"
            style={{ background: "#25D366", color: "#fff", padding: "11px", borderRadius: 10, fontWeight: 700, textDecoration: "none", fontSize: 13, textAlign: "center" }}
          >💬 WhatsApp</a>
          <Link
            to={`/navigate?dest=${event.lat},${event.lng}&code=${encodeURIComponent(event.address_civ)}&label=${encodeURIComponent(event.venue_name || event.title)}`}
            data-testid="event-navigate-btn"
            style={{ background: "#3B82F6", color: "#fff", padding: "11px", borderRadius: 10, fontWeight: 700, textDecoration: "none", fontSize: 13, textAlign: "center" }}
          >🧭 Y aller</Link>
        </div>

        {/* ADRESSE */}
        <div data-testid="event-address-block" style={{ marginTop: 22, padding: 16, background: "rgba(255,255,255,0.04)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.05em" }}>ADRESSE CIV</div>
          <div style={{ fontSize: 14, fontFamily: "monospace", color: "#fff", marginTop: 4 }}>{event.address_civ}</div>
          <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 4 }}>📍 lat {event.lat?.toFixed(5)}, lng {event.lng?.toFixed(5)}</div>
        </div>

        {/* QR CODE */}
        <div style={{ marginTop: 22, padding: 16, background: "rgba(255,255,255,0.04)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 10 }}>SCANNER POUR NAVIGUER</div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <div data-testid="event-qr" style={{ background: "#fff", padding: 10, borderRadius: 8, lineHeight: 0 }}>
              <QRCodeSVG value={navUrl} size={140} level="M" includeMargin={false} />
            </div>
            <div style={{ fontSize: 12, color: "#cbd5e1", lineHeight: 1.5 }}>
              Pointe ton téléphone vers ce QR pour ouvrir directement la navigation GPS depuis n'importe où.
              <button
                onClick={onShare}
                data-testid="event-share-btn"
                style={{ marginTop: 8, background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600, display: "block" }}
              >🔗 Partager le lien</button>
            </div>
          </div>
        </div>
      </div>

      <PaymentMockModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        event={event}
        userId={userId}
      />
    </div>
  );
}
